import React from 'react';
import { PencilIcon, CheckIcon, TrashIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import type { ProductSummary } from '../../hooks/useProviderProducts';
import { productLabel, ProductPricePosition } from '../../lib/pricePosition';
import PricePositionBadge from './PricePositionBadge';
import AutoTagChips from './AutoTagChips';

interface ProductCardProps {
  product: ProductSummary;
  onEdit: (productId: string) => void;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: (sku: string) => void;
  /** Product is on the merchant's "removed from my store" list. */
  removed?: boolean;
  onRemove?: (sku: string) => void;
  onRestore?: (sku: string) => void;
  /** Supplier's own standing on this product; absent in the merchant view. */
  pricePosition?: ProductPricePosition;
  /** Automatic tags this product gets in the merchant's store; merchant view only. */
  autoTags?: string[];
  autoTagsActive?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onEdit, selectable, selected, onToggleSelect, removed, onRemove, onRestore, pricePosition, autoTags, autoTagsActive }) => {
  // Fallback image for products without photos - using SVG data URI
  const fallbackImage = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300"%3E%3Crect fill="%23f3f4f6" width="300" height="300"/%3E%3Ctext fill="%239ca3af" font-family="monospace" font-size="18" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
  const imageSrc = product.photo_url || fallbackImage;

  const handleClick = () => {
    if (selectable && onToggleSelect) {
      onToggleSelect(product.sku);
    } else {
      onEdit(product.product_id);
    }
  };

  return (
    <div
      className={`glass-card rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group ${
        selectable && !selected ? 'opacity-50' : ''
      }`}
      onClick={handleClick}
    >
      {/* Product Image */}
      <div className="relative aspect-square bg-transparent overflow-hidden">
        <img
          src={imageSrc}
          alt={product.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = fallbackImage;
          }}
        />

        {/* Selection indicator */}
        {selectable && (
          <div className="absolute top-2 left-2 z-10">
            <div
              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                selected
                  ? 'bg-orange-500 border-orange-500'
                  : 'bg-black/40 border-white/50'
              }`}
            >
              {selected && <CheckIcon className="w-4 h-4 text-white" />}
            </div>
          </div>
        )}

        {/* Stock indicator */}
        <div className="absolute top-2 right-2">
          <div className={`w-3 h-3 rounded-full border-2 border-white ${product.in_stock ? 'bg-green-500' : 'bg-red-500'}`} />
        </div>

        {/* Edit button overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
          <button
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-black/60 p-2 rounded-full shadow-lg"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(product.product_id);
            }}
          >
            <PencilIcon className="w-5 h-5 text-orange-400" />
          </button>
          {/* Separate from the supplier checkbox on purpose: unchecking stops sourcing
              from this supplier only, removing takes the product off the storefront. */}
          {(onRemove || onRestore) && (
            <button
              className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-black/60 p-2 rounded-full shadow-lg ml-2"
              title={removed ? 'Restore to my store' : 'Remove from my store (all suppliers)'}
              aria-label={removed ? 'Restore to my store' : 'Remove from my store'}
              onClick={(e) => {
                e.stopPropagation();
                if (removed) onRestore?.(product.sku);
                else onRemove?.(product.sku);
              }}
            >
              {removed
                ? <ArrowUturnLeftIcon className="w-5 h-5 text-emerald-400" />
                : <TrashIcon className="w-5 h-5 text-red-400/80" />}
            </button>
          )}
        </div>
      </div>

      {/* Product Info */}
      <div className="p-4 space-y-2">
        {removed && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-500/10 border border-red-500/20 text-red-400">
            Removed from store
          </span>
        )}
        {/* Title */}
        <h3 className="text-sm font-semibold text-white  line-clamp-2 min-h-[2.5rem]">
          {product.title}
        </h3>

        {/* SKU Badge */}
        <div className="flex items-center gap-2">
          <span className="inline-block bg-transparent border border-white/15 px-2 py-0.5 text-xs  text-white/70 truncate">
            {product.sku}
          </span>
          <PricePositionBadge label={productLabel(pricePosition)} />
        </div>

        {/* Vendor */}
        <p className="text-xs text-white/50  truncate">
          {product.vendor}
        </p>

        <AutoTagChips tags={autoTags} active={autoTagsActive} />

        {/* Variant Count & Category */}
        <div className="flex items-center justify-between text-xs text-white/35 ">
          <span>{product.variant_count} variant{product.variant_count !== 1 ? 's' : ''}</span>
          {product.category && (
            <span className="truncate ml-2">{product.category}</span>
          )}
        </div>

        {/* Price & Stock */}
        <div className="flex items-center justify-between pt-2 border-t border-white/[0.06]">
          <div className="text-sm font-bold text-white ">
            {product.lowest_price !== null ? `€${product.lowest_price.toFixed(2)}` : 'N/A'}
          </div>
          <div className="text-xs text-white/50 ">
            Stock: {product.total_quantity}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
